import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../App";
import { Badge, Button, Card, Col, Row, Table, Tag, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import { QuestionCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Inquiry, InquiryHistoryPage } from "../models/models";
import { getInquiryHistory, getPatientAdmission, getPatients } from "../models/serverApi";
import dayjs from "dayjs";

const PAGE_SIZE = 20;

const InquiriesPage: React.FC = () => {
    const [context, setContext] = useContext(AppContext);
    const [data, setData] = useState<InquiryHistoryPage | null>(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPage(page);
    }, [page]);

    const fetchPage = (p: number) => {
        setLoading(true);
        getInquiryHistory((p - 1) * PAGE_SIZE, PAGE_SIZE).then(result => {
            setData(result);
            setLoading(false);
        });
    };

    const handleGoToField = async (inquiry: Inquiry) => {
        const [pts, admissions] = await Promise.all([getPatients(), getPatientAdmission()]);
        const patient = pts.find(p => p.id === inquiry.patientId);
        const admission = admissions.find(a => a.id === inquiry.patientAdmissionId);
        setContext(prev => ({
            ...prev,
            selection: { ...prev.selection, patient, admission } as any
        }));
        navigate(
            `/ecrf/${inquiry.ecrfId}?sectionId=${inquiry.sectionId}&fieldId=${inquiry.fieldId}`,
            { state: { patientId: inquiry.patientId, admissionId: inquiry.patientAdmissionId } }
        );
    };

    const columns = [
        {
            title: "Ημερομηνία",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm"),
            width: 140,
        },
        {
            title: "Από",
            dataIndex: "fromUserName",
            key: "fromUserName",
        },
        {
            title: "Προς",
            dataIndex: "toUserName",
            key: "toUserName",
        },
        {
            title: "Ασθενής",
            dataIndex: "patientName",
            key: "patientName",
            render: (v: string, r: Inquiry) => v ?? `#${r.patientId}`,
        },
        {
            title: "Ενότητα",
            dataIndex: "sectionName",
            key: "sectionName",
            render: (v: string, r: Inquiry) => v ?? `#${r.sectionId}`,
        },
        {
            title: "Πεδίο",
            dataIndex: "fieldLabel",
            key: "fieldLabel",
            render: (v: string, r: Inquiry) => v ?? `#${r.fieldId}`,
        },
        {
            title: "Σημείωση",
            dataIndex: "note",
            key: "note",
        },
        {
            title: "Τιμή",
            dataIndex: "fieldValueSnapshot",
            key: "fieldValueSnapshot",
        },
        {
            title: "Κατάσταση",
            dataIndex: "isResolved",
            key: "isResolved",
            render: (v: boolean) => v
                ? <Tag color="green" icon={<CheckCircleOutlined />}>Επιλύθηκε</Tag>
                : <Tag color="gold" icon={<QuestionCircleOutlined />}>Εκκρεμεί</Tag>,
            width: 130,
        },
        {
            title: "",
            key: "action",
            render: (_: any, record: Inquiry) => (
                !record.isResolved && (
                    <Button size="small" type="primary" onClick={() => handleGoToField(record)}>
                        Μετάβαση
                    </Button>
                )
            ),
            width: 110,
        },
    ];

    return (
        <Badge.Ribbon text={context.user?.admin ? "Admin" : context.user?.supervisor ? "Supervisor" : "User"}>
            <Typography.Title level={4} style={{ margin: "16px 0 8px" }}>Ιστορικό Ερωτήσεων</Typography.Title>
            <Content>
                <Row>
                    <Col span={24}>
                        <Card style={{ marginTop: "24px" }}>
                            <Table
                                loading={loading}
                                dataSource={data?.inquiries ?? []}
                                columns={columns}
                                rowKey="id"
                                pagination={{
                                    current: page,
                                    pageSize: PAGE_SIZE,
                                    total: data?.totalCount ?? 0,
                                    onChange: (p) => setPage(p),
                                    showSizeChanger: false,
                                }}
                                scroll={{ x: true }}
                            />
                        </Card>
                    </Col>
                </Row>
            </Content>
        </Badge.Ribbon>
    );
};

export default InquiriesPage;
